import {
  BoosterCodeExpiry,
  BoosterEnrollmentPeriodUnit,
  BoosterIncreaseEveryUnit,
  BoosterProgramApiTargetUserType,
  BoosterProgramStatus,
  BoosterProgramTargetUser,
  BoosterRewardType,
} from 'Modules'

export const BOOSTER_PROGRAM_STATUS: Record<BoosterProgramStatus, BoosterProgramStatus> = {
  DRAFT: 'DRAFT',
  SCHEDULED: 'SCHEDULED',
  ACTIVE: 'ACTIVE',
  PAUSED: 'PAUSED',
  EXPIRED: 'EXPIRED',
}

export const BOOSTER_PROGRAM_STATUS_CONFIG: Record<
  BoosterProgramStatus,
  { label: string; color: string; background: string }
> = {
  DRAFT: { label: 'Draft', color: '#364153', background: '#f3f4f6' },
  SCHEDULED: { label: 'Scheduled', color: '#df8e0b', background: '#fcf7dc' },
  ACTIVE: { label: 'Active', color: '#008236', background: '#dcfce7' },
  PAUSED: { label: 'Paused', color: '#4c29ea', background: '#dce1fc' },
  EXPIRED: { label: 'Expired', color: '#364153', background: '#f3f4f6' },
}

export const BOOSTER_PROGRAM_STATUS_OPTIONS: { value: BoosterProgramStatus; label: string }[] = (
  Object.entries(BOOSTER_PROGRAM_STATUS_CONFIG) as [BoosterProgramStatus, { label: string }][]
).map(([value, { label }]) => ({ value, label }))

export const BOOSTER_PROGRAM_TARGET_USER_CONFIG: Record<BoosterProgramTargetUser, string> = {
  ALL: 'All users',
  REFERRED: 'Referred users',
  OTHERS: 'Others',
}

export const BOOSTER_PROGRAM_TARGET_USER_OPTIONS: {
  value: BoosterProgramTargetUser
  label: string
}[] = (
  Object.entries(BOOSTER_PROGRAM_TARGET_USER_CONFIG) as [BoosterProgramTargetUser, string][]
).map(([value, label]) => ({ value, label }))

export const BOOSTER_REWARD_TYPE: Record<BoosterRewardType, BoosterRewardType> = {
  DISCOUNT_CODE: 'DISCOUNT_CODE',
  APR_BOOSTER_CODE: 'APR_BOOSTER_CODE',
  CASHBACK_TOKEN_CODE: 'CASHBACK_TOKEN_CODE',
  PTO_BONUS_CODE: 'PTO_BONUS_CODE',
}

export const BOOSTER_REWARD_TYPE_CONFIG: Record<BoosterRewardType, string> = {
  DISCOUNT_CODE: 'Discount Code',
  APR_BOOSTER_CODE: 'APR Booster Code',
  CASHBACK_TOKEN_CODE: 'Cashback Token Code',
  PTO_BONUS_CODE: 'PTO Bonus Code',
}

export const BOOSTER_REWARD_TYPE_OPTIONS: { value: BoosterRewardType; label: string }[] = (
  Object.entries(BOOSTER_REWARD_TYPE_CONFIG) as [BoosterRewardType, string][]
).map(([value, label]) => ({ value, label }))

export const BOOSTER_CODE_EXPIRY_CONFIG: Record<BoosterCodeExpiry, string> = {
  '5_minutes': '5 minutes from issue date',
  '10_minutes': '10 minutes from issue date',
  '1_hour': '1 hour from issue date',
  '15_days': '15 days from issue date',
  '30_days': '30 days from issue date',
  '60_days': '60 days from issue date',
  '90_days': '90 days from issue date',
}

export const BOOSTER_CODE_EXPIRY_OPTIONS: { value: BoosterCodeExpiry; label: string }[] = (
  Object.entries(BOOSTER_CODE_EXPIRY_CONFIG) as [BoosterCodeExpiry, string][]
).map(([value, label]) => ({ value, label }))

export const BOOSTER_TOKEN_TYPE_OPTIONS: { value: string; label: string }[] = [
  { value: 'MEY', label: 'Mey' },
]

export const BOOSTER_INCREASE_EVERY_UNIT_OPTIONS: {
  value: BoosterIncreaseEveryUnit
  label: string
}[] = [
  { value: 'minutes', label: 'Minutes' },
  { value: 'days', label: 'Days' },
  { value: 'weeks', label: 'Weeks' },
  { value: 'months', label: 'Months' },
]

export const BOOSTER_ENROLLMENT_PERIOD_UNIT_CONFIG: Record<BoosterEnrollmentPeriodUnit, string> = {
  minutes: 'minutes',
  months: 'months',
}

export const BOOSTER_ENROLLMENT_PERIOD_UNIT_OPTIONS: {
  value: BoosterEnrollmentPeriodUnit
  label: string
}[] = [
  { value: 'months', label: 'Months' },
  { value: 'minutes', label: 'Minutes' },
]

/** Max enrollment period when the unit is months; minutes has no upper cap. */
export const BOOSTER_ENROLLMENT_PERIOD_MONTHS_MAX = 12

export const DEFAULT_BOOSTER_ENROLLMENT_PERIOD_UNIT: BoosterEnrollmentPeriodUnit = 'months'

export const BOOSTER_PARTICIPANT_LIMIT_TYPE_OPTIONS: {
  value: 'UNLIMITED' | 'LIMITED'
  label: string
}[] = [
  { value: 'UNLIMITED', label: 'Unlimited' },
  { value: 'LIMITED', label: 'Limited' },
]

export const BOOSTER_PROGRAM_WHITELIST_MIME_TYPES = [
  'text/csv',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
]

export const BOOSTER_PROGRAM_WHITELIST_MAX_SIZE_MB = 10

export const BOOSTER_PROGRAM_WHITELIST_TEMPLATE_FILENAME = 'booster-program-whitelist-template.csv'

/** Header (and only column) the BE expects in the uploaded whitelist file. */
export const BOOSTER_PROGRAM_WHITELIST_TEMPLATE_HEADER = 'walletAddress'

/** Sample wallet addresses shown in the downloadable template so users see the expected format. */
export const BOOSTER_PROGRAM_WHITELIST_TEMPLATE_SAMPLE_ADDRESSES = [
  '0xae0cb8bef7cb23a006b0b40f2cde619706e5b961',
  '0xcc8d8cca865825a2a49b22be42c1a18a928fba8c',
  '0xe6c88d91379b66c144deec1d5ef2022ea443e6e9',
  '0x5c9e8810d5f599aa2c7fc3b9dd80b17600efb412',
  '0x790d9361a0680d31372d9ef1d7b14de339452ea1',
]

export const BOOSTER_PROGRAM_MAX_MILESTONES = 10

/**
 * Backend status codes for booster program:
 * 0 = DRAFT, 1 = ACTIVE, 2 = PAUSED, 3 = SCHEDULED, 4 = EXPIRED.
 * Unknown codes fall back to DRAFT.
 */
export const BOOSTER_PROGRAM_STATUS_FROM_CODE: Record<number, BoosterProgramStatus> = {
  0: 'DRAFT',
  1: 'ACTIVE',
  2: 'PAUSED',
  3: 'SCHEDULED',
  4: 'EXPIRED',
}

export const BOOSTER_PROGRAM_STATUS_TO_CODE = {
  DRAFT: 0,
  ACTIVE: 1,
  PAUSED: 2,
  SCHEDULED: 3,
  EXPIRED: 4,
  DELETE: 5,
} as const satisfies Record<BoosterProgramStatus | 'DELETE', number>

/** Milestone status codes — separate enum from program: 0 = deleted, 1 = active. */
export const BOOSTER_MILESTONE_STATUS_CODE = {
  DELETED: 0,
  ACTIVE: 1,
} as const

export const BOOSTER_PROGRAM_TARGET_USER_FROM_API: Record<
  BoosterProgramApiTargetUserType,
  BoosterProgramTargetUser
> = {
  all_users: 'ALL',
  referred_users: 'REFERRED',
  others: 'OTHERS',
}

export const BOOSTER_PROGRAM_TARGET_USER_TO_API: Record<
  BoosterProgramTargetUser,
  BoosterProgramApiTargetUserType
> = {
  ALL: 'all_users',
  REFERRED: 'referred_users',
  OTHERS: 'others',
}

export const BOOSTER_PROGRAM_MESSAGES = {
  E01: (fieldName: string) => `${fieldName} cannot be smaller than 1`,
  E02: (fieldName: string) => `${fieldName} cannot be greater than 12`,
  E03: 'File type must be CSV, XLSX, XLS',
  E04: 'File size cannot be greater than 10MB',
  E05: 'End date cannot be smaller than start date',
  E06: (fieldName: string) => `${fieldName} cannot be greater than 1,000,000`,
  E07: 'Purchase quantity of this milestone must be greater than purchase quantity of previous milestone',
  E08: 'Purchase quantity of this milestone must be smaller than purchase quantity of next milestone',
  E09: 'Start date cannot be in the past',
  S01: 'Create booster program successfully',
  S02: 'Save booster program as a draft successfully',
  S03: 'Delete booster program successfully',
}

export const BOOSTER_PROGRAM_FIELD_MAX_LENGTH = {
  programTitle: 255,
  description: 3000,
  terms: 3000,
  userEnrollmentPeriod: 2,
  limitNumber: 10,
  startingFillNumber: 3,
  increaseByNumber: 3,
  purchaseQuantity: 5,
  discountValue: 2,
  boostAprValue: 2,
  cashbackRate: 2,
  bonusValue: 10,
  totalSupply: 10,
  walletSearch: 255,
}
